import axios from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeLatest, delay } from 'redux-saga/effects'; 
import wooCommerceApi from '../../integrations/woocommerce/api';
import {
  fetchProductReviewsFailure,
  fetchProductReviewsRequest,
  fetchProductReviewsSuccess,
  ProductReview,
  SubmitReviewPayload,
  submitProductReviewFailure,
  submitProductReviewRequest,
  submitProductReviewSuccess,
  clearReviewSubmitStatus
} from './review.slice';
import { refreshProductRating } from '../../integrations/woocommerce/productSlice';

interface WooProductReview {
  id: number;
  product_id: number;
  date_created: string;
  status: string;
  reviewer: string;
  reviewer_email?: string;
  review: string;
  rating?: number;
}

const PRODUCT_REVIEWS_ENDPOINT = '/products/reviews';

const parseRating = (review: WooProductReview): number => {
  const directRating = (review as WooProductReview & { rating?: number | string }).rating;
  if (typeof directRating === 'number') return directRating;
  if (typeof directRating === 'string') {
    const parsed = Number(directRating);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const stripHtml = (value?: string): string => (value || '').replace(/<[^>]+>/g, '').trim();

const mapWooReviewToReview = (review: WooProductReview): ProductReview => ({
  id: review.id,
  productId: review.product_id,
  authorName: review.reviewer,
  authorEmail: review.reviewer_email,
  date: review.date_created,
  rating: parseRating(review),
  comment: stripHtml(review.review),
  status: review.status,
});

const fetchProductReviewsApi = async (productId: number): Promise<ProductReview[]> => {
  const response = await wooCommerceApi.get<WooProductReview[]>(PRODUCT_REVIEWS_ENDPOINT, {
    params: {
      product: productId,
      per_page: 100,
      order: 'desc',
      status: 'approved',
    },
  });
  return Array.isArray(response.data) ? response.data.map(mapWooReviewToReview) : [];
};

const postProductReviewApi = async (payload: SubmitReviewPayload): Promise<void> => {
  const { productId, name, email, rating, comment } = payload;
  await wooCommerceApi.post(
    PRODUCT_REVIEWS_ENDPOINT,
    {
      product_id: productId,
      review: comment,
      reviewer: name,
      reviewer_email: email,
      rating,
    },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    },
  );
};

export function* fetchProductReviewsWorker(action: PayloadAction<number>) {
  const productId = action.payload;
  try {
    const reviews: ProductReview[] = yield call(fetchProductReviewsApi, productId);
    yield put(fetchProductReviewsSuccess({ productId, reviews }));

    const ratedReviews = reviews.filter((review) => review.rating > 0);
    const ratingCount = ratedReviews.length;
    const averageRating =
      ratingCount > 0
        ? (ratedReviews.reduce((sum, review) => sum + review.rating, 0) / ratingCount).toFixed(1)
        : '0';

    yield put(refreshProductRating({ productId, averageRating, ratingCount }));
  } catch (error) {
    const message = axios.isAxiosError(error)
      ? error.response?.data?.message || error.message
      : error instanceof Error
        ? error.message
        : 'Failed to fetch product reviews';
    yield put(fetchProductReviewsFailure({ productId, error: message }));
  }
}

export function* submitProductReviewWorker(action: PayloadAction<SubmitReviewPayload>) {
  try {
    yield call(postProductReviewApi, action.payload);
    yield put(submitProductReviewSuccess());
    yield put(fetchProductReviewsRequest(action.payload.productId));
    
    yield delay(3000); 
    
    yield put(clearReviewSubmitStatus());
    
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('[submitProductReviewWorker] WooCommerce error:', error.response?.data);
    }
    const message = axios.isAxiosError(error)
      ? error.response?.data?.message || error.message
      : error instanceof Error
        ? error.message
        : 'Failed to submit review';
    yield put(submitProductReviewFailure(message));
  }
}

export function* watchReviewActions() {
  yield takeLatest(fetchProductReviewsRequest.type, fetchProductReviewsWorker);
  yield takeLatest(submitProductReviewRequest.type, submitProductReviewWorker);
}